// Guarded localStorage access for UI preferences and other per-browser
// state. Reads validate and fall back; writes and clears swallow storage
// failures (private mode, blocked storage, quota) so the value simply lives
// for the session instead of crashing the UI.

/**
 * Read a preference from localStorage.
 *
 * `parse` turns the raw stored string into the preference value; return
 * `null` (or throw) for malformed input and the `fallback` is used. Also
 * falls back when storage is unavailable (SSR, private mode) or empty.
 */
export function readLocalPreference<T>(
  key: string,
  parse: (raw: string) => T | null,
  fallback: T,
): T {
  if (typeof window === 'undefined') return fallback
  try {
    const raw = window.localStorage.getItem(key)
    if (raw === null) return fallback
    return parse(raw) ?? fallback
  } catch {
    return fallback
  }
}

/** Write a preference; a failed write is silently ignored. */
export function writeLocalPreference(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value)
  } catch {
    // Storage unavailable — the preference still applies for the session.
  }
}

/** Remove a preference; a failed removal is silently ignored. */
export function clearLocalPreference(key: string): void {
  try {
    window.localStorage.removeItem(key)
  } catch {
    // Storage unavailable — there is nothing this browser lets us clear.
  }
}
